import {
  AdditionalInfoStep,
  EnglishProficiencyStep,
  LocationSelectionStep,
  TrainingDateStep,
  UploadDocumentsStep
} from '../../../../components'
import { getStoredResumeEnrollment } from '../../utils/storage'
import {
  getSelfEnrollmentDisplayStep,
  SELF_ENROLLMENT_DISPLAY_TOTAL_STEPS,
  SELF_ENROLLMENT_STEPS
} from '../../utils/steps'
import type { FlowStepRendererProps, SharedStepProps } from './types'

type JourneyStepProps = Pick<
  FlowStepRendererProps,
  | 'currentStep'
  | 'fieldEditability'
  | 'formData'
  | 'updateFormData'
  | 'locationStateMismatchWarning'
  | 'isCheckingStepAccess'
  | 'enrollmentState'
> & {
  stepProps: SharedStepProps
}

export function renderJourneyStep({
  currentStep,
  fieldEditability,
  formData,
  updateFormData,
  locationStateMismatchWarning,
  isCheckingStepAccess,
  enrollmentState,
  stepProps
}: JourneyStepProps) {
  switch (currentStep) {
    case SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY:
      return (
        <EnglishProficiencyStep
          {...stepProps}
          onPassed={enrollmentState.handleEnglishAssessmentPassed}
          enrollment={getStoredResumeEnrollment()}
        />
      )
    case SELF_ENROLLMENT_STEPS.LOCATION_SELECTION:
      return (
        <LocationSelectionStep
          currentStep={getSelfEnrollmentDisplayStep(currentStep)}
          totalSteps={SELF_ENROLLMENT_DISPLAY_TOTAL_STEPS}
          fieldEditability={fieldEditability}
          formData={formData}
          updateFormData={updateFormData}
          onBack={enrollmentState.handleBack}
          onContinue={enrollmentState.handleLocationSelectionContinue}
          errorMessage={enrollmentState.stepSubmissionError}
          warningMessage={locationStateMismatchWarning}
          continueDisabled={Boolean(locationStateMismatchWarning)}
        />
      )
    case SELF_ENROLLMENT_STEPS.ADDITIONAL_INFO:
      return (
        <AdditionalInfoStep
          currentStep={getSelfEnrollmentDisplayStep(currentStep)}
          totalSteps={SELF_ENROLLMENT_DISPLAY_TOTAL_STEPS}
          fieldEditability={fieldEditability}
          formData={formData}
          updateFormData={updateFormData}
          onBack={enrollmentState.handleBack}
          onContinue={shouldShowLaptopQuestion => {
            void enrollmentState.handleAdditionalInfoContinue(shouldShowLaptopQuestion)
          }}
          onContinueWithoutEndorsements={shouldShowLaptopQuestion => {
            void enrollmentState.handleAdditionalInfoContinueWithoutEndorsements(
              shouldShowLaptopQuestion
            )
          }}
          isSubmitting={
            enrollmentState.updateEnrollmentEndorsementsPending ||
            enrollmentState.submitEnrollmentStepPending ||
            isCheckingStepAccess
          }
          errorMessage={enrollmentState.stepSubmissionError}
        />
      )
    case SELF_ENROLLMENT_STEPS.TRAINING_DATE:
      return (
        <TrainingDateStep
          currentStep={getSelfEnrollmentDisplayStep(currentStep)}
          totalSteps={SELF_ENROLLMENT_DISPLAY_TOTAL_STEPS}
          fieldEditability={fieldEditability}
          trainingStartDate={formData.trainingStartDate}
          trainingMode={formData.trainingMode}
          onTrainingStartDateChange={trainingStartDate => updateFormData({ trainingStartDate })}
          yardDays={formData.yardDays}
          onYardDaysChange={yardDays => updateFormData({ yardDays })}
          onBack={enrollmentState.handleBack}
          onContinue={enrollmentState.handleTrainingDateContinue}
          isSubmitting={enrollmentState.submitEnrollmentStepPending || isCheckingStepAccess}
          errorMessage={enrollmentState.stepSubmissionError}
          locationExternalId={formData.location || null}
        />
      )
    case SELF_ENROLLMENT_STEPS.REMOVED_TUITION_SUMMARY:
    case SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS:
      return (
        <UploadDocumentsStep
          onNext={enrollmentState.handleDocumentsContinue}
          onBack={enrollmentState.handleBack}
          currentStep={getSelfEnrollmentDisplayStep(currentStep)}
          totalSteps={SELF_ENROLLMENT_DISPLAY_TOTAL_STEPS}
        />
      )
    default:
      return null
  }
}
